import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import { Legislation } from "../models/legislation/Legislation";
import { handlePaginationQuery } from "./crudService";
import { RestClient } from "./restClient";
import RestResponse from "@/models/client/RestResponse";
import { LegislationSourceKey } from "@/config/datasources";
import RequestMethod from "@/models/client/RequestMethod";
import { TimelineItem } from "@/models/legislation/Timeline";
import type { LegislationPdfLink } from "@/models/legislation/LegislationPdfLink";
import type {
  LegislationSummaryRequestItem,
  ResolvedLegislationSummary,
} from "@/models/legislation/LegislationSummary";

// Type for source-specific filters: { sourceName: { filterKey: [values] } }
export type SourceFilters = Record<string, Record<string, string[]>>;

export type LegislationServiceType = {
  getPaginated: (
    params: PaginationQuery,
    sources?: LegislationSourceKey[],
    sourceFilters?: SourceFilters,
    signal?: AbortSignal
  ) => RestResponse<Pagination<Legislation>>;
  getById: (id: string) => RestResponse<Legislation>;
  getByExternalId: (
    externalId: string,
    datasource: string
  ) => RestResponse<Legislation>;
  getTimelineByExternalId: (externalId: string) => RestResponse<TimelineItem[]>;
  /**
   * A short-lived, read-only link to one of the legislation's own PDFs.
   * `pdfPath` is a storage path exactly as the legislation records it.
   */
  getPdfLink: (
    externalId: string,
    datasource: string,
    pdfPath: string,
    signal?: AbortSignal
  ) => RestResponse<LegislationPdfLink>;
  /**
   * Resolves up to `LEGISLATION_SUMMARY_BATCH_LIMIT` documents to their list
   * fields in one request, without their html. Records no views.
   */
  resolveSummaries: (
    items: LegislationSummaryRequestItem[],
    signal?: AbortSignal
  ) => RestResponse<ResolvedLegislationSummary[]>;
};

const legislationServices = (
  client: RestClient,
  path: string
): LegislationServiceType => ({
  getPaginated: async (
    params: PaginationQuery,
    sources?: LegislationSourceKey[],
    sourceFilters?: SourceFilters,
    signal?: AbortSignal
  ) => {
    const sourcesQuery = sources?.length
      ? sources.map((source) => `&Sources=${source}`).join("")
      : "";

    // Serialize source-specific filters as JSON
    const sourceFiltersQuery = sourceFilters && Object.keys(sourceFilters).length > 0
      ? `&SourceFilters=${encodeURIComponent(JSON.stringify(sourceFilters))}`
      : "";

    const response = await client.fetch<Pagination<Legislation>>(
      RequestMethod.GET,
      `${path}?${handlePaginationQuery(params)}${sourcesQuery}${sourceFiltersQuery}`,
      undefined,
      undefined,
      undefined,
      undefined,
      {
        signal,
      }
    );
    return response;
  },
  getById: async (id: string) =>
    await client.fetch<Legislation>(RequestMethod.GET, `${path}/${id}`),
  getByExternalId: async (externalId: string, datasource: string) => {
    return await client.fetch<Legislation>(
      RequestMethod.GET,
      `${path}/externalid/${externalId}/datasource/${datasource}`
    );
  },
  getTimelineByExternalId: async (externalId: string) =>
    await client.fetch<TimelineItem[]>(
      RequestMethod.GET,
      `${path}/external_id/${externalId}/timeline`
    ),
  getPdfLink: async (externalId, datasource, pdfPath, signal) =>
    await client.fetch<LegislationPdfLink>(
      RequestMethod.GET,
      `${path}/externalid/${encodeURIComponent(externalId)}/datasource/${encodeURIComponent(datasource)}/pdf-link?path=${encodeURIComponent(pdfPath)}`,
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),
  resolveSummaries: async (items, signal) =>
    await client.fetch<ResolvedLegislationSummary[]>(
      RequestMethod.POST,
      `${path}/resolve-summaries`,
      items,
      undefined,
      undefined,
      undefined,
      { signal }
    ),
});

export default legislationServices;
